import { useRef, useState } from "react";
import { PageHeader, Card, Button, Input } from "@/components/ui/Primitives";
import { formatCents } from "@/lib/api";
import { Calculator, Wallet, ArrowRight, Plus, X } from "lucide-react";

export default function RentCalculator() {
  const [incomes, setIncomes] = useState([{ label: "Job 1", amount: "" }]);
  const [debt, setDebt] = useState("");
  const [savings, setSavings] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const resultRef = useRef(null);

  const monthlyIncome = incomes.reduce((sum, i) => sum + Number(i.amount || 0), 0);
  const monthlyDebt = Number(debt || 0);
  const totalSavings = Number(savings || 0);

  const safeRent = monthlyIncome * 0.30;
  const stretchRent = monthlyIncome * 0.35;
  const dti = monthlyIncome > 0 ? (monthlyDebt / monthlyIncome) * 100 : 0;
  const adjustedSafe = Math.max(0, safeRent - monthlyDebt * 0.5);
  const monthsCushion = safeRent > 0 ? Math.floor(totalSavings / safeRent) : 0;

  const addIncome = () => {
    setIncomes((prev) => [...prev, { label: `Income ${prev.length + 1}`, amount: "" }]);
    setSubmitted(false);
  };
  const removeIncome = (idx) => {
    setIncomes((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));
  };
  const updateIncome = (idx, patch) => {
    setIncomes((prev) => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };

  const calculate = (e) => {
    e?.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const reset = () => {
    setIncomes([{ label: "Job 1", amount: "" }]);
    setDebt(""); setSavings(""); setSubmitted(false);
  };

  return (
    <div>
      <PageHeader title="Rent calculator" subtitle="See what you can comfortably afford — add up to as many income sources as you need."/>

      <Card className="p-6 max-w-2xl">
        <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Your finances</h2>
        <form onSubmit={calculate} className="space-y-4">
          <div className="space-y-3">
            <div className="text-sm font-medium text-slate-700 flex items-center justify-between">
              <span>Monthly income sources (CAD)</span>
              <span className="text-xs text-slate-500" data-testid="income-total">Total: {formatCents(monthlyIncome * 100)}</span>
            </div>
            {incomes.map((inc, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`income-row-${idx}`}>
                <input
                  value={inc.label}
                  onChange={(e) => updateIncome(idx, { label: e.target.value })}
                  placeholder="Source"
                  className="w-1/3 border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-green-800 focus:ring-2 focus:ring-green-800/15 outline-none"
                  data-testid={`income-label-${idx}`}
                />
                <input
                  type="number"
                  value={inc.amount}
                  onChange={(e) => updateIncome(idx, { amount: e.target.value })}
                  placeholder="0.00"
                  className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-green-800 focus:ring-2 focus:ring-green-800/15 outline-none"
                  data-testid={`income-amount-${idx}`}
                />
                {incomes.length > 1 && (
                  <button type="button" onClick={() => removeIncome(idx)} data-testid={`income-remove-${idx}`}
                    className="p-2 text-slate-400 hover:text-red-600 rounded">
                    <X className="w-4 h-4"/>
                  </button>
                )}
              </div>
            ))}
            <Button type="button" variant="ghost" onClick={addIncome} data-testid="add-income-button" className="!px-3 !py-1.5 text-xs">
              <Plus className="w-3.5 h-3.5"/> Add another income source
            </Button>
          </div>
          <Input label="Monthly debt payments (CAD)" type="number" value={debt} onChange={(e) => setDebt(e.target.value)} placeholder="400" testid="calc-debt-input"/>
          <Input label="Savings on hand (CAD)" type="number" value={savings} onChange={(e) => setSavings(e.target.value)} placeholder="3000" testid="calc-savings-input"/>
          <p className="text-xs text-slate-500 leading-relaxed">
            We use the 30 % rule (rent ≤ 30 % of gross income) recommended by the Canada Mortgage and Housing Corporation, adjusted for your debt service load. Add multiple income sources (job + side gig + partner) and we'll sum them automatically.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button type="submit" disabled={!monthlyIncome} data-testid="calc-submit-button" className="!px-6">
              <Calculator className="w-4 h-4"/> Calculate
            </Button>
            {submitted && (
              <Button type="button" variant="ghost" onClick={reset} data-testid="calc-reset-button">Reset</Button>
            )}
          </div>
        </form>
      </Card>

      {submitted && monthlyIncome > 0 && (
        <div ref={resultRef} className="mt-6">
          <Card className="p-6 bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0 shadow-lg shadow-emerald-500/20">
            <div className="flex items-center gap-2 text-emerald-200">
              <Calculator className="w-4 h-4"/>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold">Recommended max rent</div>
            </div>
            <div className="mt-3 font-display text-5xl font-bold" data-testid="calc-safe-rent">{formatCents(Math.round(adjustedSafe * 100))}</div>
            <div className="mt-1 text-white/70 text-sm">Comfortable monthly rent for your combined household income of {formatCents(monthlyIncome * 100)}</div>

            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-200 font-bold">Stretch budget</div>
                <div className="font-display text-2xl font-bold mt-1">{formatCents(Math.round(stretchRent * 100))}</div>
                <div className="text-xs text-white/70">35 % of income — tighter</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-200 font-bold">Months of cushion</div>
                <div className="font-display text-2xl font-bold mt-1" data-testid="calc-cushion">{monthsCushion}</div>
                <div className="text-xs text-white/70">From your savings</div>
              </div>
              <div className="col-span-2 pt-3 border-t border-white/10">
                <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-200 font-bold">Debt-to-income</div>
                <div className="font-display text-2xl font-bold mt-1">{dti.toFixed(0)}%</div>
                <div className="text-xs text-white/70">Lenders prefer under 36 %.</div>
              </div>
            </div>
          </Card>

          <Card className="p-6 mt-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-none"><Wallet className="w-5 h-5"/></div>
              <div>
                <h3 className="font-display text-base font-bold text-slate-900">Plan ahead for move-in costs</h3>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                  In most Canadian provinces, landlords can request first and last month's rent plus a damage deposit (where allowed). Plan for ~ 2.5× your monthly rent in upfront cash.
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
